import React, { useState, useEffect, useRef } from 'react';
import { invoiceAPI, settingsAPI } from '../../services/api';
import Navbar from '../../components/Navbar';

const PlatformSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('company');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [uploading, setUploading] = useState({ logo: false, signature: false });
  const logoInputRef = useRef(null);
  const signatureInputRef = useRef(null);
  const [formData, setFormData] = useState({
    // Company Information
    name: '',
    legalName: '',
    gstin: '',
    pan: '',
    cin: '',
    address: {
      street: '',
      city: '',
      state: '',
      stateCode: '',
      zipCode: ''
    },
    // Contact Details
    contactEmail: '',
    supportEmail: '',
    supportPhone: '',
    website: '',
    // Invoice Branding
    logo: '',
    signature: '',
    authorizedSignatory: '',
    // Tax Rates (18% GST on platform fee and delivery fee)
    foodGst: 5,
    platformGst: 18,
    deliveryGst: 18,
    advertisingGst: 18,
    tdsRate194O: 0.1,
    // Nil-rated (0% GST) menu-item categories — comma-separated names, e.g. "Frozen Items".
    gstExemptCategories: '',
    // HSN/SAC Codes
    hsnCodes: {
      food: { code: '', description: '' },
      delivery: { code: '', description: '' },
      platformFee: { code: '', description: '' },
      advertising: { code: '', description: '' },
      subscription: { code: '', description: '' }
    },
    // Invoice Prefixes
    invoicePrefixes: {
      customer_food: '',
      platform_service_fee: '',
      advertising_marketing: '',
      subscription: '',
      tds_statement: '',
      credit_note: '',
      debit_note: '',
      settlement_statement: ''
    },
    // Terms & Conditions
    customerTerms: '',
    restaurantTerms: '',
    footerNote: ''
  });

  const theme = {
    primaryColor: '#FF6961',
    primaryDark: '#E6524A',
    textPrimary: '#0F172A',
    textSecondary: '#64748B',
    backgroundColor: '#F8FAFC'
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await invoiceAPI.getPlatformSettings();
      // Backend returns nested structure: { platformCompany, invoicePrefixes, invoiceTerms, taxRates, hsnSacCodes }
      const data = response.data?.data || response.data || {};
      setSettings(data);

      // Extract from nested backend structure to flat formData
      const platformCompany = data.platformCompany || {};
      const invoicePrefixes = data.invoicePrefixes || {};
      const invoiceTerms = data.invoiceTerms || {};
      const taxRates = data.taxRates || {};
      const hsnSacCodes = data.hsnSacCodes || {};

      setFormData(prev => ({
        ...prev,
        // Platform Company fields (flat)
        name: platformCompany.name || prev.name,
        legalName: platformCompany.legalName || prev.legalName,
        gstin: platformCompany.gstin || prev.gstin,
        pan: platformCompany.pan || prev.pan,
        cin: platformCompany.cin || prev.cin,
        address: { ...prev.address, ...(platformCompany.address || {}) },
        // Contact Details
        contactEmail: platformCompany.contactEmail || prev.contactEmail,
        supportEmail: platformCompany.supportEmail || prev.supportEmail,
        supportPhone: platformCompany.supportPhone || prev.supportPhone,
        website: platformCompany.website || prev.website,
        // Branding
        logo: platformCompany.logo || prev.logo,
        signature: platformCompany.signature || prev.signature,
        authorizedSignatory: platformCompany.authorizedSignatory || prev.authorizedSignatory,
        // Tax Rates
        foodGst: taxRates.foodGst ?? prev.foodGst,
        platformGst: taxRates.platformGst ?? prev.platformGst,
        deliveryGst: taxRates.deliveryGst ?? prev.deliveryGst,
        advertisingGst: taxRates.advertisingGst ?? prev.advertisingGst,
        tdsRate194O: taxRates.tdsRate194O ?? prev.tdsRate194O,
        gstExemptCategories: Array.isArray(taxRates.gstExemptCategories) ? taxRates.gstExemptCategories.join(', ') : (prev.gstExemptCategories || ''),
        // HSN/SAC Codes (backend uses hsnSacCodes)
        hsnCodes: { ...prev.hsnCodes, ...hsnSacCodes },
        // Invoice Prefixes
        invoicePrefixes: { ...prev.invoicePrefixes, ...invoicePrefixes },
        // Terms & Conditions
        customerTerms: invoiceTerms.customerTerms || prev.customerTerms,
        restaurantTerms: invoiceTerms.restaurantTerms || prev.restaurantTerms,
        footerNote: invoiceTerms.footerNote || prev.footerNote
      }));
      setMessage({ type: '', text: '' });
    } catch (error) {
      console.error('Failed to fetch platform settings:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to fetch platform settings'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNestedChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value
      }
    }));
  };

  const handleDoubleNestedChange = (parent, child, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [child]: {
          ...prev[parent][child],
          [field]: value
        }
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      // Convert flat formData to nested structure expected by backend
      const payload = {
        platformCompany: {
          name: formData.name,
          legalName: formData.legalName,
          gstin: formData.gstin,
          pan: formData.pan,
          cin: formData.cin,
          address: formData.address,
          contactEmail: formData.contactEmail,
          supportEmail: formData.supportEmail,
          supportPhone: formData.supportPhone,
          website: formData.website,
          logo: formData.logo,
          signature: formData.signature,
          authorizedSignatory: formData.authorizedSignatory
        },
        taxRates: {
          foodGst: parseFloat(formData.foodGst) || 5,
          platformGst: parseFloat(formData.platformGst) || 18,
          deliveryGst: parseFloat(formData.deliveryGst) || 18,
          advertisingGst: parseFloat(formData.advertisingGst) || 18,
          tdsRate194O: parseFloat(formData.tdsRate194O) || 0.1,
          gstExemptCategories: (formData.gstExemptCategories || '').split(',').map(s => s.trim()).filter(Boolean)
        },
        hsnSacCodes: formData.hsnCodes, // Frontend uses hsnCodes, backend uses hsnSacCodes
        invoicePrefixes: formData.invoicePrefixes,
        invoiceTerms: {
          customerTerms: formData.customerTerms,
          restaurantTerms: formData.restaurantTerms,
          footerNote: formData.footerNote
        }
      };

      await invoiceAPI.updatePlatformSettings(payload);
      await fetchSettings();
      setMessage({ type: 'success', text: 'Platform settings updated successfully!' });

      // Auto-hide success message after 3 seconds
      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 3000);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to update platform settings'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (type) => {
    const inputRef = type === 'logo' ? logoInputRef : signatureInputRef;
    const file = inputRef.current?.files?.[0];

    if (!file) {
      setMessage({ type: 'error', text: 'Please select a file to upload' });
      return;
    }

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      setMessage({ type: 'error', text: 'Invalid file type. Please upload PNG, JPG, or SVG' });
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'File size must be less than 2MB' });
      return;
    }

    setUploading(prev => ({ ...prev, [type]: true }));
    setMessage({ type: '', text: '' });

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', type);

      const response = await settingsAPI.uploadBrandingImage(formData);
      const uploadedUrl = response.data?.data?.url || response.data?.url;

      if (uploadedUrl) {
        // Update the form data with the new URL
        setFormData(prev => ({
          ...prev,
          [type]: uploadedUrl
        }));
        setMessage({ type: 'success', text: `${type.charAt(0).toUpperCase() + type.slice(1)} uploaded successfully!` });

        // Clear the file input
        inputRef.current.value = '';

        // Auto-hide success message after 3 seconds
        setTimeout(() => {
          setMessage({ type: '', text: '' });
        }, 3000);
      }
    } catch (error) {
      console.error(`Failed to upload ${type}:`, error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || `Failed to upload ${type}`
      });
    } finally {
      setUploading(prev => ({ ...prev, [type]: false }));
    }
  };

  const tabs = [
    { id: 'company', label: 'Company Information', icon: '🏢' },
    { id: 'contact', label: 'Contact Details', icon: '📞' },
    { id: 'branding', label: 'Invoice Branding', icon: '🎨' },
    { id: 'tax', label: 'Tax Rates', icon: '💰' },
    { id: 'hsn', label: 'HSN/SAC Codes', icon: '📋' },
    { id: 'prefixes', label: 'Invoice Prefixes', icon: '🔢' },
    { id: 'terms', label: 'Terms & Conditions', icon: '📄' }
  ];

  if (loading) {
    return (
      <div style={styles.layout}>
        <Navbar />
        <div style={styles.content}>
          <div style={styles.loading}>Loading platform settings...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.layout}>
      <Navbar />
      <div style={styles.content}>
        <div style={styles.header}>
          <h1 style={styles.title}>Platform Settings</h1>
          <p style={styles.subtitle}>Manage platform company details for invoice system</p>
        </div>

        {message.text && (
          <div style={{
            ...styles.message,
            ...(message.type === 'success' ? styles.messageSuccess : styles.messageError)
          }}>
            {message.text}
          </div>
        )}

        <div style={styles.container}>
          {/* Tab Navigation */}
          <div style={styles.tabContainer}>
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  ...styles.tab,
                  ...(activeTab === tab.id ? styles.tabActive : {})
                }}
              >
                <span style={styles.tabIcon}>{tab.icon}</span>
                <span style={styles.tabLabel}>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div style={styles.tabContent}>
            {/* Company Information Tab */}
            {activeTab === 'company' && (
              <div style={styles.section}>
                <h2 style={styles.sectionTitle}>Company Information</h2>

                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Brand Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData?.name || ''}
                      onChange={handleInputChange}
                      style={styles.input}
                      placeholder="FoodWinc"
                    />
                    <small style={styles.helpText}>Customer-facing brand name shown on app and invoices header</small>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Legal Entity Name *</label>
                    <input
                      type="text"
                      name="legalName"
                      value={formData?.legalName || ''}
                      onChange={handleInputChange}
                      style={styles.input}
                      placeholder="AG Traders"
                    />
                    <small style={styles.helpText}>Registered business name (shown as issuer on invoices, linked with GSTIN/PAN)</small>
                  </div>
                </div>

                <div style={styles.infoBox}>
                  <strong>Brand vs Legal Entity:</strong>
                  <ul style={styles.infoList}>
                    <li><strong>Brand Name:</strong> Your customer-facing name (e.g., "FoodWinc") - shown prominently on app and invoice headers</li>
                    <li><strong>Legal Entity Name:</strong> Your registered business name (e.g., "AG Traders") - used as invoice issuer and linked with GSTIN/PAN</li>
                    <li>If different, invoices will show: "FoodWinc, a brand operated by AG Traders"</li>
                  </ul>
                </div>

                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>GSTIN *</label>
                    <input
                      type="text"
                      name="gstin"
                      value={formData?.gstin || ''}
                      onChange={handleInputChange}
                      style={styles.input}
                      placeholder="22AAAAA0000A1Z5"
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>PAN</label>
                    <input
                      type="text"
                      name="pan"
                      value={formData?.pan || ''}
                      onChange={handleInputChange}
                      style={styles.input}
                      placeholder="AAAAA0000A"
                    />
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>CIN</label>
                  <input
                    type="text"
                    name="cin"
                    value={formData?.cin || ''}
                    onChange={handleInputChange}
                    style={styles.input}
                    placeholder="U74999DL2020PTC123456"
                  />
                </div>

                <h3 style={styles.subsectionTitle}>Registered Address</h3>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Street Address *</label>
                  <input
                    type="text"
                    value={formData?.address?.street || ''}
                    onChange={(e) => handleNestedChange('address', 'street', e.target.value)}
                    style={styles.input}
                    placeholder="123, Sample Street, Sample Area"
                  />
                </div>

                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>City *</label>
                    <input
                      type="text"
                      value={formData?.address?.city || ''}
                      onChange={(e) => handleNestedChange('address', 'city', e.target.value)}
                      style={styles.input}
                      placeholder="Mumbai"
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>State *</label>
                    <input
                      type="text"
                      value={formData?.address?.state || ''}
                      onChange={(e) => handleNestedChange('address', 'state', e.target.value)}
                      style={styles.input}
                      placeholder="Maharashtra"
                    />
                  </div>
                </div>

                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>State Code *</label>
                    <input
                      type="text"
                      value={formData?.address?.stateCode || ''}
                      onChange={(e) => handleNestedChange('address', 'stateCode', e.target.value)}
                      style={styles.input}
                      placeholder="27"
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>ZIP Code *</label>
                    <input
                      type="text"
                      value={formData?.address?.zipCode || ''}
                      onChange={(e) => handleNestedChange('address', 'zipCode', e.target.value)}
                      style={styles.input}
                      placeholder="400001"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Contact Details Tab */}
            {activeTab === 'contact' && (
              <div style={styles.section}>
                <h2 style={styles.sectionTitle}>Contact Details</h2>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Contact Email *</label>
                  <input
                    type="email"
                    name="contactEmail"
                    value={formData?.contactEmail || ''}
                    onChange={handleInputChange}
                    style={styles.input}
                    placeholder="contact@foodwinc.com"
                  />
                  <small style={styles.helpText}>Primary contact email for business communications</small>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Support Email *</label>
                  <input
                    type="email"
                    name="supportEmail"
                    value={formData?.supportEmail || ''}
                    onChange={handleInputChange}
                    style={styles.input}
                    placeholder="support@foodwinc.com"
                  />
                  <small style={styles.helpText}>Customer support email shown on invoices</small>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Support Phone *</label>
                  <input
                    type="tel"
                    name="supportPhone"
                    value={formData?.supportPhone || ''}
                    onChange={handleInputChange}
                    style={styles.input}
                    placeholder="+91-1234567890"
                  />
                  <small style={styles.helpText}>Customer support phone number</small>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Website</label>
                  <input
                    type="url"
                    name="website"
                    value={formData?.website || ''}
                    onChange={handleInputChange}
                    style={styles.input}
                    placeholder="https://www.foodwinc.com"
                  />
                  <small style={styles.helpText}>Company website URL</small>
                </div>
              </div>
            )}

            {/* Invoice Branding Tab */}
            {activeTab === 'branding' && (
              <div style={styles.section}>
                <h2 style={styles.sectionTitle}>Invoice Branding</h2>

                {/* Company Logo */}
                <div style={styles.formGroup}>
                  <label style={styles.label}>Company Logo</label>
                  <div style={styles.uploadRow}>
                    <input
                      type="url"
                      name="logo"
                      value={formData?.logo || ''}
                      onChange={handleInputChange}
                      style={{ ...styles.input, flex: 1 }}
                      placeholder="https://example.com/logo.png"
                    />
                    <input
                      type="file"
                      ref={logoInputRef}
                      accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                      style={{ display: 'none' }}
                      onChange={() => handleUpload('logo')}
                    />
                    <button
                      type="button"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={uploading.logo}
                      style={{
                        ...styles.uploadButton,
                        ...(uploading.logo ? styles.uploadButtonDisabled : {})
                      }}
                    >
                      {uploading.logo ? 'Uploading...' : '📤 Upload'}
                    </button>
                  </div>
                  <small style={styles.helpText}>URL to company logo image (displayed on invoices)</small>
                  {formData?.logo && (
                    <div style={styles.imagePreview}>
                      <img
                        src={formData.logo}
                        alt="Logo Preview"
                        style={styles.previewImage}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  )}
                </div>

                {/* Signature Image */}
                <div style={styles.formGroup}>
                  <label style={styles.label}>Signature Image</label>
                  <div style={styles.uploadRow}>
                    <input
                      type="url"
                      name="signature"
                      value={formData?.signature || ''}
                      onChange={handleInputChange}
                      style={{ ...styles.input, flex: 1 }}
                      placeholder="https://example.com/signature.png"
                    />
                    <input
                      type="file"
                      ref={signatureInputRef}
                      accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                      style={{ display: 'none' }}
                      onChange={() => handleUpload('signature')}
                    />
                    <button
                      type="button"
                      onClick={() => signatureInputRef.current?.click()}
                      disabled={uploading.signature}
                      style={{
                        ...styles.uploadButton,
                        ...(uploading.signature ? styles.uploadButtonDisabled : {})
                      }}
                    >
                      {uploading.signature ? 'Uploading...' : '📤 Upload'}
                    </button>
                  </div>
                  <small style={styles.helpText}>URL to authorized signatory's signature image</small>
                  {formData?.signature && (
                    <div style={styles.imagePreview}>
                      <img
                        src={formData.signature}
                        alt="Signature Preview"
                        style={styles.previewSignature}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  )}
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Authorized Signatory Name *</label>
                  <input
                    type="text"
                    name="authorizedSignatory"
                    value={formData?.authorizedSignatory || ''}
                    onChange={handleInputChange}
                    style={styles.input}
                    placeholder="John Doe"
                  />
                  <small style={styles.helpText}>Name of the person authorized to sign invoices</small>
                </div>

                <div style={styles.infoBox}>
                  <strong>📝 Branding Guidelines:</strong>
                  <ul style={styles.infoList}>
                    <li>Upload logo and signature images directly using the Upload button</li>
                    <li>Supported formats: PNG, JPG, SVG (max 2MB)</li>
                    <li>Recommended logo size: 200x80 pixels</li>
                    <li>Signature image should be clear and professional</li>
                    <li>After uploading, click "Save Settings" to apply changes</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Tax Rates Tab */}
            {activeTab === 'tax' && (
              <div style={styles.section}>
                <h2 style={styles.sectionTitle}>Tax Rates</h2>
                <p style={styles.description}>Configure GST and TDS rates for various services</p>

                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Food GST Rate (%) *</label>
                    <input
                      type="number"
                      name="foodGst"
                      value={formData?.foodGst ?? 5}
                      onChange={handleInputChange}
                      style={styles.input}
                      min="0"
                      max="100"
                      step="0.01"
                    />
                    <small style={styles.helpText}>GST rate for food items (typically 5%)</small>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Platform Fee GST Rate (%) *</label>
                    <input
                      type="number"
                      name="platformGst"
                      value={formData?.platformGst ?? 18}
                      onChange={handleInputChange}
                      style={styles.input}
                      min="0"
                      max="100"
                      step="0.01"
                    />
                    <small style={styles.helpText}>GST rate for platform commission (typically 18%)</small>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Delivery GST Rate (%) *</label>
                    <input
                      type="number"
                      name="deliveryGst"
                      value={formData?.deliveryGst ?? 18}
                      onChange={handleInputChange}
                      style={styles.input}
                      min="0"
                      max="100"
                      step="0.01"
                    />
                    <small style={styles.helpText}>GST rate for delivery fee (typically 18%)</small>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Advertising GST Rate (%) *</label>
                    <input
                      type="number"
                      name="advertisingGst"
                      value={formData?.advertisingGst ?? 18}
                      onChange={handleInputChange}
                      style={styles.input}
                      min="0"
                      max="100"
                      step="0.01"
                    />
                    <small style={styles.helpText}>GST rate for advertising services (typically 18%)</small>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>TDS Rate 194O (%) *</label>
                  <input
                    type="number"
                    name="tdsRate194O"
                    value={formData?.tdsRate194O ?? 0.1}
                    onChange={handleInputChange}
                    style={styles.input}
                    min="0"
                    max="20"
                    step="0.01"
                  />
                  <small style={styles.helpText}>TDS rate under Section 194-O (0.1% effective Oct 1, 2024 — Budget 2024)</small>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Nil-rated (0% GST) Categories</label>
                  <input
                    type="text"
                    name="gstExemptCategories"
                    value={formData?.gstExemptCategories ?? ''}
                    onChange={handleInputChange}
                    style={styles.input}
                    placeholder="e.g. Frozen Items, Frozen Desserts"
                  />
                  <small style={styles.helpText}>
                    Comma-separated menu-item category names that are charged <strong>0% GST</strong> (nil-rated,
                    e.g. packaged “Frozen Items”). Matched by exact category name (case-insensitive). The restaurant
                    is still paid the full item value — only GST is dropped. Leave blank to tax all food normally.
                  </small>
                </div>

                <div style={styles.warningBox}>
                  <strong>⚠️ Important:</strong> Tax rate changes will apply to all new invoices. Existing invoices will retain their original tax rates.
                </div>
              </div>
            )}

            {/* HSN/SAC Codes Tab */}
            {activeTab === 'hsn' && (
              <div style={styles.section}>
                <h2 style={styles.sectionTitle}>HSN/SAC Codes</h2>
                <p style={styles.description}>Configure HSN/SAC codes for different service categories</p>

                {['food', 'delivery', 'platformFee', 'advertising', 'subscription'].map(category => (
                  <div key={category} style={styles.hsnSection}>
                    <h3 style={styles.subsectionTitle}>
                      {category.charAt(0).toUpperCase() + category.slice(1).replace(/([A-Z])/g, ' $1')}
                    </h3>
                    <div style={styles.formRow}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Code *</label>
                        <input
                          type="text"
                          value={formData?.hsnCodes?.[category]?.code || ''}
                          onChange={(e) => handleDoubleNestedChange('hsnCodes', category, 'code', e.target.value)}
                          style={styles.input}
                          placeholder="996331"
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Description *</label>
                        <input
                          type="text"
                          value={formData?.hsnCodes?.[category]?.description || ''}
                          onChange={(e) => handleDoubleNestedChange('hsnCodes', category, 'description', e.target.value)}
                          style={styles.input}
                          placeholder="Service description"
                        />
                      </div>
                    </div>
                  </div>
                ))}

                <div style={styles.infoBox}>
                  <strong>📋 HSN/SAC Code Guidelines:</strong>
                  <ul style={styles.infoList}>
                    <li>HSN codes are for goods, SAC codes are for services</li>
                    <li>Codes must be valid as per GST regulations</li>
                    <li>Food items typically use HSN codes</li>
                    <li>Services (delivery, platform fee) use SAC codes</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Invoice Prefixes Tab */}
            {activeTab === 'prefixes' && (
              <div style={styles.section}>
                <h2 style={styles.sectionTitle}>Invoice Prefixes</h2>
                <p style={styles.description}>Configure prefixes for different invoice types</p>

                <div style={styles.formGrid}>
                  {Object.keys(formData.invoicePrefixes).map(key => (
                    <div key={key} style={styles.formGroup}>
                      <label style={styles.label}>
                        {key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')} *
                      </label>
                      <input
                        type="text"
                        value={formData?.invoicePrefixes?.[key] || ''}
                        onChange={(e) => handleNestedChange('invoicePrefixes', key, e.target.value)}
                        style={styles.input}
                        placeholder={key.toUpperCase().replace(/_/g, '-')}
                      />
                      <small style={styles.helpText}>
                        Example: {formData.invoicePrefixes[key] || key.toUpperCase().replace(/_/g, '-')}/2024/0001
                      </small>
                    </div>
                  ))}
                </div>

                <div style={styles.warningBox}>
                  <strong>⚠️ Note:</strong> Changing prefixes will only affect new invoices. Existing invoice numbers will remain unchanged.
                </div>
              </div>
            )}

            {/* Terms & Conditions Tab */}
            {activeTab === 'terms' && (
              <div style={styles.section}>
                <h2 style={styles.sectionTitle}>Terms & Conditions</h2>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Customer Terms & Conditions</label>
                  <textarea
                    name="customerTerms"
                    value={formData?.customerTerms || ''}
                    onChange={handleInputChange}
                    style={styles.textarea}
                    rows={8}
                    placeholder="Enter terms and conditions for customer invoices..."
                  />
                  <small style={styles.helpText}>Displayed on customer food order invoices</small>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Restaurant Terms & Conditions</label>
                  <textarea
                    name="restaurantTerms"
                    value={formData?.restaurantTerms || ''}
                    onChange={handleInputChange}
                    style={styles.textarea}
                    rows={8}
                    placeholder="Enter terms and conditions for restaurant invoices..."
                  />
                  <small style={styles.helpText}>Displayed on restaurant service invoices (commission, advertising, etc.)</small>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Footer Note</label>
                  <textarea
                    name="footerNote"
                    value={formData?.footerNote || ''}
                    onChange={handleInputChange}
                    style={styles.textarea}
                    rows={3}
                    placeholder="Thank you for your business!"
                  />
                  <small style={styles.helpText}>Short note displayed at the bottom of all invoices</small>
                </div>

                <div style={styles.infoBox}>
                  <strong>📄 Terms Guidelines:</strong>
                  <ul style={styles.infoList}>
                    <li>Keep terms clear and concise</li>
                    <li>Include payment terms and deadlines</li>
                    <li>Mention refund and cancellation policies</li>
                    <li>Ensure compliance with applicable laws</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Save Button */}
            <div style={styles.saveButtonContainer}>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  ...styles.saveButton,
                  ...(saving ? styles.saveButtonDisabled : {})
                }}
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  layout: {
    minHeight: '100vh',
    backgroundColor: '#F8FAFC'
  },
  content: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '20px'
  },
  header: {
    marginBottom: '30px'
  },
  title: {
    fontSize: '2rem',
    fontWeight: '700',
    color: '#0F172A',
    marginBottom: '8px'
  },
  subtitle: {
    fontSize: '1rem',
    color: '#64748B',
    margin: 0
  },
  loading: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#64748B',
    fontSize: '1.1rem'
  },
  message: {
    padding: '12px 20px',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '0.95rem',
    fontWeight: '500'
  },
  messageSuccess: {
    backgroundColor: '#D1FAE5',
    color: '#065F46',
    border: '1px solid #A7F3D0'
  },
  messageError: {
    backgroundColor: '#FEE2E2',
    color: '#991B1B',
    border: '1px solid #FECACA'
  },
  container: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden'
  },
  tabContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    borderBottom: '2px solid #E2E8F0',
    backgroundColor: '#F8FAFC',
    padding: '10px 10px 0 10px'
  },
  tab: {
    flex: '1 1 auto',
    minWidth: '120px',
    padding: '12px 16px',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: '500',
    color: '#64748B',
    borderBottom: '3px solid transparent',
    transition: 'all 0.2s',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px'
  },
  tabActive: {
    color: '#FF6961',
    borderBottomColor: '#FF6961',
    backgroundColor: 'white'
  },
  tabIcon: {
    fontSize: '1.2rem'
  },
  tabLabel: {
    fontSize: '0.85rem'
  },
  tabContent: {
    padding: '30px'
  },
  section: {
    maxWidth: '900px'
  },
  sectionTitle: {
    fontSize: '1.5rem',
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: '8px'
  },
  subsectionTitle: {
    fontSize: '1.2rem',
    fontWeight: '600',
    color: '#0F172A',
    marginTop: '24px',
    marginBottom: '16px'
  },
  description: {
    color: '#64748B',
    marginBottom: '24px',
    fontSize: '0.95rem'
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '20px',
    marginBottom: '20px'
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '20px',
    marginBottom: '20px'
  },
  formGroup: {
    marginBottom: '20px'
  },
  label: {
    display: 'block',
    fontSize: '0.9rem',
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: '8px'
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '0.95rem',
    border: '1px solid #E2E8F0',
    borderRadius: '6px',
    outline: 'none',
    transition: 'border-color 0.2s',
    color: '#0F172A'
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '0.95rem',
    border: '1px solid #E2E8F0',
    borderRadius: '6px',
    outline: 'none',
    transition: 'border-color 0.2s',
    color: '#0F172A',
    fontFamily: 'inherit',
    resize: 'vertical'
  },
  helpText: {
    display: 'block',
    marginTop: '4px',
    fontSize: '0.85rem',
    color: '#64748B'
  },
  hsnSection: {
    padding: '20px',
    backgroundColor: '#F8FAFC',
    borderRadius: '8px',
    marginBottom: '16px'
  },
  infoBox: {
    backgroundColor: '#EFF6FF',
    border: '1px solid #BFDBFE',
    borderRadius: '8px',
    padding: '16px',
    marginTop: '24px',
    color: '#1E3A8A',
    fontSize: '0.9rem'
  },
  warningBox: {
    backgroundColor: '#FEF3C7',
    border: '1px solid #FCD34D',
    borderRadius: '8px',
    padding: '16px',
    marginTop: '24px',
    color: '#92400E',
    fontSize: '0.9rem'
  },
  infoList: {
    marginTop: '8px',
    marginBottom: '0',
    paddingLeft: '20px'
  },
  saveButtonContainer: {
    marginTop: '30px',
    paddingTop: '20px',
    borderTop: '2px solid #E2E8F0',
    display: 'flex',
    justifyContent: 'flex-end'
  },
  saveButton: {
    padding: '12px 32px',
    fontSize: '1rem',
    fontWeight: '600',
    color: 'white',
    backgroundColor: '#FF6961',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxShadow: '0 2px 4px rgba(255, 105, 97, 0.2)'
  },
  saveButtonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed'
  },
  uploadRow: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center'
  },
  uploadButton: {
    padding: '10px 16px',
    fontSize: '0.9rem',
    fontWeight: '600',
    color: 'white',
    backgroundColor: '#3B82F6',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    whiteSpace: 'nowrap'
  },
  uploadButtonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
    backgroundColor: '#93C5FD'
  },
  imagePreview: {
    marginTop: '12px',
    padding: '12px',
    backgroundColor: '#F8FAFC',
    borderRadius: '8px',
    border: '1px solid #E2E8F0'
  },
  previewImage: {
    maxWidth: '200px',
    maxHeight: '80px',
    objectFit: 'contain'
  },
  previewSignature: {
    maxWidth: '150px',
    maxHeight: '60px',
    objectFit: 'contain'
  }
};

// Add hover effect via inline style (using onMouseEnter/onMouseLeave would be needed for full interactivity)
export default PlatformSettings;
